import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  TableBody,
  TableHeaderRow,
  TableCell,
  TableRow,
  TableHeader,
  Badge,
  Button,
  MailIcon,
  Text,
} from '@razorpay/blade/components';
import { TableHeadCell } from 'components/ApprovalWorkflows/MyRequests/components/Table/styles';
import { useHistory } from 'react-router-dom';
import { useSendReminder } from './hooks/useSendReminder';
import { SendReminderModal } from './SendReminderModal';
import { useQuery } from 'react-query';
import { MISSING_REPORT_SUMMARY } from 'api/missingInformationReport';
import { MissingReportReminderType } from 'api/types/missingInformationReportTypes';
import api from 'api';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { missingInformationSummaryTableData, sendReminderModalDescriptions } from './data';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { routePaths } from 'components/Routes/data';

export const SummaryList = () => {
  const [reminderType, setReminderType] = useState<MissingReportReminderType>();
  const history = useHistory();
  const { isSendReminderModalOpen, onOpenModal, onCloseModal, sendReminder, isSendingReminder } =
    useSendReminder();

  const {
    data: missingReportSummary,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [MISSING_REPORT_SUMMARY],
    queryFn: api.missingInformationReport.fetchMissingReportSummary,
  });

  const shouldShowEmptyView =
    !isError &&
    !!missingReportSummary?.data &&
    Object.values(missingReportSummary.data).every((value) => value === 0);

  const shouldShowSummary = !isError && !shouldShowEmptyView;

  const moveToBankApprovals = () => {
    history.push(routePaths.approvalWorkflows.myRequests.employeeBankAccount.pendingApprovals);
  };

  return (
    <Box>
      <Heading size="large" weight="semibold" color="surface.text.gray.normal">
        Missing Information Report
      </Heading>
      <Box marginTop="spacing.8">
        {isError && <ErrorView onRefetch={refetch} />}
        {shouldShowEmptyView && (
          <EmptyListView
            title="🔍 No Missing Information Found"
            description="Great! All employees have completed their details."
          />
        )}
        {shouldShowSummary && (
          <Table
            isLoading={isLoading}
            isRefreshing={isFetching}
            data={{
              nodes: missingInformationSummaryTableData.filter(
                (item) => missingReportSummary && missingReportSummary?.data[item.type] > 0,
              ),
            }}
            showBorderedCells={false}
            gridTemplateColumns="1fr 1fr 1fr">
            {(nodes) => (
              <>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeadCell>Missing Data</TableHeadCell>
                    <TableHeadCell>Employee Count</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {nodes.map((item) => (
                    <TableRow
                      key={item.id}
                      item={item}
                      hoverActions={
                        <Box
                          display="flex"
                          gap="spacing.4"
                          backgroundColor="surface.background.gray.intense">
                          {item.type === 'unverified_bank_account_details' ? (
                            <Button variant="secondary" size="xsmall" onClick={moveToBankApprovals}>
                              View Approvals
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="tertiary"
                                icon={MailIcon}
                                iconPosition="left"
                                size="xsmall"
                                onClick={() => {
                                  if (item.type !== 'unverified_bank_account_details') {
                                    onOpenModal();
                                    setReminderType(item.type);
                                  }
                                }}>
                                Send Reminders
                              </Button>
                              <Button
                                variant="secondary"
                                size="xsmall"
                                onClick={() => {
                                  history.push(item.url);
                                }}>
                                View Employees
                              </Button>
                            </>
                          )}
                        </Box>
                      }>
                      <TableCell>
                        <Text weight="regular">{item.title}</Text>
                      </TableCell>
                      <TableCell>
                        <Text weight="regular">{missingReportSummary?.data[item.type]}</Text>
                      </TableCell>
                      <TableCell>
                        {item.priority === 'high' ? (
                          <Badge color="negative">High Priority</Badge>
                        ) : (
                          <Badge color="positive">Low Priority</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </>
            )}
          </Table>
        )}
      </Box>
      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        onClose={onCloseModal}
        description={reminderType ? sendReminderModalDescriptions[reminderType] : ''}
        isSendingReminder={isSendingReminder}
        onSendReminder={() => {
          if (reminderType) {
            sendReminder({
              reminder_type: reminderType,
            });
          }
        }}
      />
    </Box>
  );
};
